import { ReactNode, useEffect, useRef, useState } from 'react';
import { FileField, Image, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { icon_pause, icon_play } from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components';

import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';
import { useVideoSource } from 'utils/hooks/useVideoSource';

type VideoBackgroundProps = {
  videoLg: FileField;
  videoMd: FileField;
  videoXs: FileField;
  image: ImageField;
  backgroundContrast: number;
  reducedWidth: boolean;
  playerOpened?: boolean;
  children: ReactNode;
  videoContainerClass?: string;
  hideControlsOnMobile?: boolean;
};

const VideoBackground = ({
  videoLg,
  videoMd,
  videoXs,
  image,
  backgroundContrast,
  reducedWidth,
  playerOpened = false,
  videoContainerClass = 'aspect-video',
  hideControlsOnMobile = false,
  children,
}: VideoBackgroundProps) => {
  const { t } = useFallbackTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const videoSrcXs = useVideoSource([videoXs, videoMd, videoLg]);
  const videoSrcMd = useVideoSource([videoMd, videoLg, videoXs]);
  const videoSrcLg = useVideoSource([videoLg, videoMd, videoXs]);

  const hasVideoSrc = videoSrcXs || videoSrcMd || videoSrcLg;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      if (playerOpened) {
        video.pause();
        setIsPaused(true);
      } else {
        video.play();
        setIsPaused(false);
      }
    }
  }, [playerOpened]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoad = () => setIsLoaded(true);

    if (video.readyState >= 4) {
      handleLoad();
      return;
    }

    video.addEventListener('loadeddata', handleLoad);
    video.addEventListener('canplay', handleLoad);

    return () => {
      video.removeEventListener('loadeddata', handleLoad);
      video.removeEventListener('canplay', handleLoad);
    };
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPaused) {
        void video.play();
      } else {
        video.pause();
      }
      setIsPaused(!isPaused);
    }
  };

  return (
    <div className={classNames(reducedWidth && 'container')}>
      <div className={classNames('relative w-full', videoContainerClass)}>
        {!isLoaded && (
          <div className="absolute left-0 top-0 h-full w-full">
            {image && (
              <Image
                field={image}
                className={classNames('h-full w-full object-cover', reducedWidth && 'rounded-3xl')}
              />
            )}
          </div>
        )}
        {hasVideoSrc && (
          <video
            ref={videoRef}
            className={classNames(
              `absolute left-0 top-0 h-full w-full object-cover`,
              isLoaded ? 'block' : 'hidden',
              reducedWidth && 'rounded-3xl'
            )}
            autoPlay
            loop
            playsInline
            muted
          >
            {videoSrcXs && <source src={videoSrcXs} type="video/mp4" media="(max-width: 640px)" />}
            {videoSrcMd && (
              <source
                src={videoSrcMd}
                type="video/mp4"
                media="(min-width: 641px) and (max-width: 1280px)"
              />
            )}
            {videoSrcLg && <source src={videoSrcLg} type="video/mp4" media="(min-width: 1281px)" />}
          </video>
        )}
        <div
          className={classNames(
            'relative flex min-h-full w-full !max-w-none flex-col items-center justify-center',
            reducedWidth && 'rounded-3xl'
          )}
          style={{
            backgroundColor: `rgba(0, 0, 0, ${backgroundContrast ? backgroundContrast / 100 : '0.4'})`,
          }}
        >
          {children}
          {isLoaded && (
            <button
              aria-label={isPaused ? t('system.Play') : t('system.Pause')}
              className={classNames(
                'absolute bottom-6 left-6 z-10 h-12 w-12 items-center justify-center',
                hideControlsOnMobile ? 'hidden md:flex' : 'flex',
                'md:right-auto md:top-auto lg:bottom-9 lg:left-9'
              )}
              onClick={togglePlayPause}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-solid border-white text-white transition-colors duration-300 ease-linear hover:bg-white hover:text-black">
                {isPaused ? (
                  <JabraIcon viewBox="0 0 24 24" className="h-6 w-6" Icon={icon_play} />
                ) : (
                  <JabraIcon viewBox="0 0 24 24" className="h-6 w-6" Icon={icon_pause} />
                )}
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoBackground;
